import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core";

// FAQ shown via a "?" button in the chat header (top-right). Chose the
// header-button entry point over the empty-chat landing screen because the
// landing area already has WelcomeScreen content + suggestion chips, and the
// tips stay accessible mid-conversation this way.

var FAQ_ITEMS = [
  {
    q: "What kind of questions can I ask?",
    body: (
      <p>
        Anything about your contracts, CLINs, customers, BUs, revenue, cost, margin, labor, utilization, forecast vs actuals, or rates.
        Ask in plain English &mdash; no SQL, no jargon required.
      </p>
    ),
  },
  {
    q: "Scout vs Strategist — which should I use?",
    body: (
      <div>
        <p>
          <strong>Scout</strong> handles the vast majority of what you&rsquo;ll ever need &mdash; pulling actuals vs forecast, breakdowns by contract, CLIN, customer, BU, or employee, rate and margin lookups, trend summaries, comparisons, top/bottom lists, and follow-up drill-downs. It&rsquo;s fast, precise, and purpose-built for financial data. Most power users never leave Scout.
        </p>
        <p>
          <strong>Strategist</strong> is for the rare cases that need extended reasoning &mdash; multi-step &ldquo;why&rdquo; questions, synthesizing patterns across many contracts, or building a narrative that requires thinking beyond the numbers.
        </p>
        <p style={{ marginBottom: 0 }}>
          When in doubt, start with Scout. It&rsquo;s faster and handles the vast majority of financial questions.
        </p>
      </div>
    ),
  },
  {
    q: "How specific should I be?",
    body: (
      <p>
        The more concrete, the better. <code>"How is contract 22402 doing YTD?"</code> beats <code>"How are we doing?"</code>
        {" "}If you know the contract ID, CLIN, customer, or time window &mdash; say it.
      </p>
    ),
  },
  {
    q: "Can I ask multiple things at once?",
    body: (
      <p>
        Prefer one question per message. The assistant remembers the last several turns, so ask the first question, then follow up:
        {" "}<code>"now break that down by customer"</code>, <code>"exclude FFP"</code>, <code>"what's driving the bottom 3?"</code>
      </p>
    ),
  },
  {
    q: "What time period does it use by default?",
    body: (
      <p>
        Year-to-date actuals plus full-year Live-EAC forecast, side by side. If you want a narrower window, say so &mdash; e.g.
        {" "}<code>"in Q2"</code>, <code>"for July 2026"</code>, <code>"last 3 months"</code>.
      </p>
    ),
  },
  {
    q: "How do I get a breakdown vs a summary?",
    body: (
      <p>
        Use phrases like <code>"by contract"</code>, <code>"by CLIN"</code>, <code>"by employee"</code>, <code>"by LCAT"</code>,
        {" "}<code>"by customer"</code>, <code>"by BU"</code>, or <code>"top 10…"</code>. Say <code>"summarize"</code> if you want
        the headline only.
      </p>
    ),
  },
  {
    q: "What's the difference between rate questions?",
    body: (
      <ul>
        <li><strong>Base rate</strong> &mdash; the contracted rate on the CLIN</li>
        <li><strong>Effective rate</strong> &mdash; actual dollars &divide; actual hours</li>
        <li><strong>Top-paid</strong> &mdash; highest hourly rate</li>
        <li><strong>Highest spend</strong> &mdash; highest total dollars</li>
        <li>Use the exact phrase that matches what you want.</li>
      </ul>
    ),
  },
  {
    q: "What words should I use for money concepts?",
    body: (
      <p>
        Be precise: <code>revenue</code>, <code>direct cost</code>, <code>gross margin</code>, <code>net margin</code>,
        {" "}<code>indirect</code>, <code>burdened cost</code>, <code>EAC</code>. Avoid vague terms like
        {" "}<code>"profit"</code> or <code>"cost"</code> on their own &mdash; they're ambiguous.
      </p>
    ),
  },
  {
    q: "How do I compare things?",
    body: (
      <p>
        Name both sides explicitly. <code>"Compare CP vs T&amp;M vs FFP work"</code>,
        {" "}<code>"actuals vs forecast for contract X"</code>, <code>"this year vs last year"</code>.
      </p>
    ),
  },
  {
    q: "How do I ask \"why\" questions?",
    body: (
      <p>
        Anchor them to something concrete. <code>"Why did margin drop on contract X?"</code> works better than
        {" "}<code>"why are we underperforming?"</code> The more specific the anchor, the deeper the analysis.
      </p>
    ),
  },
  {
    q: "Can I control the chart?",
    body: (
      <p>
        Yes &mdash; say <code>"show as a bar chart"</code>, <code>"as a table"</code>, or <code>"no chart, just the number"</code>.
        {" "}Otherwise the assistant picks what fits the answer.
      </p>
    ),
  },
  {
    q: "What if the answer looks wrong?",
    body: (
      <p>
        Push back in the next message. <code>"Is that burdened or unburdened?"</code>, <code>"Are you including indirect?"</code>,
        {" "}<code>"Does that include forecast or just actuals?"</code> The assistant will recheck and clarify.
      </p>
    ),
  },
  {
    q: "What can't it do?",
    body: (
      <ul>
        <li>Break down FFP revenue per employee or per account (FFP is a fixed price, not allocated)</li>
        <li>Show employee PII (names, salaries, hire dates)</li>
        <li>Modify any data &mdash; it's strictly read-only</li>
      </ul>
    ),
  },
  {
    q: "Any quick tips for better answers?",
    body: (
      <ul>
        <li>Use the same words you'd use on a financial report</li>
        <li>Mention the contract / CLIN / customer if you know it</li>
        <li>Ask the simple version first, then drill in with follow-ups</li>
        <li>If you want a specific lens (BU, customer, time window), say so upfront</li>
      </ul>
    ),
  },
];

var useStyles = makeStyles(function () {
  return {
    paper: {
      borderRadius: "12px",
      maxWidth: "640px",
      width: "100%",
      margin: "16px",
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      padding: "20px 24px 8px 24px",
      borderBottom: "1px solid #f0f0f0",
    },
    headerText: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    title: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#111827",
      lineHeight: 1.2,
      fontFamily: "'DM Sans', sans-serif",
      margin: 0,
    },
    intro: {
      fontSize: "12px",
      color: "#6b7280",
      fontFamily: "'IBM Plex Sans', sans-serif",
      lineHeight: 1.5,
      margin: 0,
    },
    closeBtn: {
      padding: "4px",
      color: "#9ca3af",
      marginTop: "-4px",
      marginRight: "-8px",
    },
    list: {
      padding: "8px 16px 20px 16px",
      maxHeight: "70vh",
      overflowY: "auto",
    },
    item: {
      borderBottom: "1px solid #f3f4f6",
    },
    itemLast: {
      borderBottom: "none",
    },
    qBtn: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      padding: "12px 8px",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: "'DM Sans', sans-serif",
      fontSize: "13px",
      fontWeight: 600,
      color: "#111827",
      lineHeight: 1.4,
      borderRadius: "6px",
      transition: "background 150ms",
      "&:hover": {
        background: "#f9fafb",
      },
    },
    chevron: {
      flexShrink: 0,
      color: "#9ca3af",
      transition: "transform 200ms ease",
    },
    chevronOpen: {
      transform: "rotate(180deg)",
    },
    answer: {
      padding: "0 8px 14px 8px",
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: "12.5px",
      color: "#374151",
      lineHeight: 1.6,
      "& p": {
        margin: "0 0 8px 0",
      },
      "& p:last-child": {
        marginBottom: 0,
      },
      "& ul": {
        margin: "4px 0 0 0",
        paddingLeft: "20px",
      },
      "& li": {
        marginBottom: "4px",
      },
      "& li:last-child": {
        marginBottom: 0,
      },
      "& code": {
        fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "11.5px",
        background: "#eff6ff",
        color: "#1e3a5f",
        padding: "1px 6px",
        borderRadius: "4px",
        border: "1px solid #dbeafe",
        whiteSpace: "nowrap",
      },
      "& strong": {
        color: "#111827",
        fontWeight: 600,
      },
    },
  };
});

var FAQDialog = function FAQDialog({ open, onClose }) {
  var classes = useStyles();
  var [openIdx, setOpenIdx] = useState(-1);

  useEffect(function () {
    if (!open) {
      setOpenIdx(-1);
    }
  }, [open]);

  var toggle = function (i) {
    setOpenIdx(openIdx === i ? -1 : i);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      aria-labelledby="faq-dialog-title"
    >
      <div className={classes.header}>
        <div className={classes.headerText}>
          <h2 id="faq-dialog-title" className={classes.title}>Get the best answers</h2>
          <p className={classes.intro}>
            A few tips on how to ask &mdash; the assistant is most accurate when questions are specific and use the right financial terms.
          </p>
        </div>
        <IconButton className={classes.closeBtn} onClick={onClose} size="small" aria-label="Close">
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>
      <div className={classes.list}>
        {FAQ_ITEMS.map(function (item, i) {
          var isOpen = openIdx === i;
          var isLast = i === FAQ_ITEMS.length - 1;
          return (
            <div key={i} className={isLast ? classes.itemLast : classes.item}>
              <button
                className={classes.qBtn}
                onClick={function () { toggle(i); }}
                aria-expanded={isOpen}
              >
                <span>{item.q}</span>
                <svg
                  className={isOpen ? `${classes.chevron} ${classes.chevronOpen}` : classes.chevron}
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {isOpen && <div className={classes.answer}>{item.body}</div>}
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};

export default FAQDialog;
